<template>
  <div class="email__email-sidebar h-full">
    <div class="m-6 clearfix">
      <vs-button class="bg-primary-gradient w-full" icon-pack="feather" icon="icon-edit"
                 @click="showComposeModal">Compose
      </vs-button>
    </div>

    <!-- compose email -->
    <vs-prompt class="email-compose" title="New Message" accept-text="Send" @cancel="clearFields" @accept="sendMail" @close="clearFields"
               :is-valid="validateForm" :active.sync="activePrompt">
      <component :is="scrollbarTag" class="scroll-area p-4" :settings="settings" :key="$vs.rtl">
        <form @submit.prevent>
          <v-select taggable multiple no-drop v-model="mailTo" placeholder="To" class="w-full mb-6"/>
          <vs-input name="mailSubject" label-placeholder="Subject" v-model="mailSubject" class="w-full mb-6"/>
          <v-select taggable multiple no-drop v-model="mailCC" placeholder="CC" class="w-full mb-6"/>
          <v-select taggable multiple no-drop v-model="mailBCC" placeholder="BCC" class="w-full mb-6"/>
          <quill-editor v-model="mailMessage" :options="editorOption"/>
          <vs-upload multiple text="Attachments" :show-upload-button="false" class="compose-mail-attachment" ref="new_file_attachment"/>
        </form>
      </component>
    </vs-prompt>

    <vs-prompt class="email-compose" title="Reply Message" accept-text="Reply" @cancel="clearReplyFields" @accept="sendReply" @close="clearReplyFields"
               :is-valid="validateReplyForm" :active.sync="activeReplyPrompt">
      <component :is="scrollbarTag" class="scroll-area p-4" :settings="settings" :key="$vs.rtl">
        <form @submit.prevent>
          <v-select taggable multiple no-drop v-model="mailReplyTo" placeholder="Reply To" class="w-full mb-6"/>
          <vs-input name="mailSubject" label-placeholder="Subject" v-model="mailReplySubject" class="w-full mb-6"/>
          <v-select taggable multiple no-drop v-model="mailReplyCC" placeholder="CC" class="w-full mb-6"/>
          <v-select taggable multiple no-drop v-model="mailReplyBCC" placeholder="BCC" class="w-full mb-6"/>
          <quill-editor v-model="mailReplyMessage" :options="editorOption"/>
          <vs-upload multiple text="Attachments" :show-upload-button="false" class="compose-mail-attachment" ref="reply_file_attachment"/>
        </form>
      </component>
    </vs-prompt>

    <vs-prompt class="email-compose" title="Forward Message" accept-text="Forward" @cancel="clearForwardFields" @accept="sendForward" @close="clearForwardFields"
               :is-valid="validateForwardForm" :active.sync="activeForwardPrompt">
      <component :is="scrollbarTag" class="scroll-area p-4" :settings="settings" :key="$vs.rtl">
        <form @submit.prevent>
          <v-select taggable multiple no-drop v-model="mailForwardTo" placeholder="Forward To" class="w-full mb-6"/>
          <vs-input name="mailSubject" label-placeholder="Subject" v-model="mailForwardSubject" class="w-full mb-6"/>
          <v-select taggable multiple no-drop v-model="mailForwardCC" placeholder="CC" class="w-full mb-6"/>
          <v-select taggable multiple no-drop v-model="mailForwardBCC" placeholder="BCC" class="w-full mb-6"/>
          <quill-editor v-model="mailForwardMessage" :options="editorOption"/>
          <vs-upload multiple text="Attachments" :show-upload-button="false" class="compose-mail-attachment" ref="forward_file_attachment"/>
        </form>
      </component>
    </vs-prompt>

    <component :is="scrollbarTag" class="email-filter-scroll-area" :settings="settings" :key="$vs.rtl">
      <div class="px-6 pb-2 flex flex-col">

        <!-- inbox -->
        <router-link v-for="folder in focusedMailFolders" tag="span" :to="`${baseUrl}/${folder.id}`" class="flex justify-between items-center cursor-pointer mt-2"
                     :class="{'text-primary': mailFilter === 'inbox'}" :key="folder.id">
          <div class="flex items-center mb-2">
            <feather-icon :icon="folder.icon" :svgClasses="[{'text-primary stroke-current': mailFilter === folder.id}, 'h-6 w-6']"/>
            <span class="text-lg ml-3">{{ folder.realName }}</span>
          </div>
          <template v-if="folder.unreadItemCount">
            <vs-chip class="number" color="primary" v-if="folder.unreadItemCount > 0">{{ folder.unreadItemCount }}</vs-chip>
          </template>
        </router-link>

      </div>
      <vs-divider></vs-divider>
      <div class="email__labels px-6 py-4">
        <h5 class="mb-8">Folders</h5>
        <!-- spam -->
        <router-link v-for="folder in otherMailFolders" tag="span" :to="`${baseUrl}/${folder.id}`" class="flex justify-between items-center cursor-pointer mt-2 "
                     :class="{'text-primary': mailFilter === 'inbox'}" :key="folder.id" :style="folder.style">
          <div class="flex items-center mb-2">
            <feather-icon :icon="'FolderIcon'" :svgClasses="[{'text-primary stroke-current': mailFilter === folder.id}, 'h-6 w-6']"/>
            <span class="text-lg ml-3">{{ folder.displayName }}</span>
          </div>
          <template v-if="folder.unreadItemCount">
            <vs-chip class="number" color="primary" v-if="folder.unreadItemCount > 0">{{ folder.unreadItemCount }}</vs-chip>
          </template>
        </router-link>
      </div>
    </component>
  </div>
</template>

<script>
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import {quillEditor} from 'vue-quill-editor';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import FeatherIcon from "./components/FeatherIcon";
import "vue-select/src/scss/vue-select.scss";
import vSelect from 'vue-select';

export default {
  props: {
    mailFilter: {type: String},
    isShowReplyPrompt: {type: Boolean},
    isShowForwardPrompt: {type: Boolean},
    openMailId: {validator: prop => typeof prop === 'string' || prop === null}
  },
  components: {
    quillEditor,
    VuePerfectScrollbar,
    FeatherIcon,
    vSelect
  },
  data() {
    return {
      activePrompt: false,
      mailTo: [],
      mailSubject: '',
      mailCC: [],
      mailBCC: [],
      mailMessage: '<br><br><p style="font-family: Calibri,sans-serif; font-size: 16px; color: #333333;">Kind Regards - ' + this.$store.state.auth.me.first_name + ' ' +
          this.$store.state.auth.me.surname + ' <span style="color: #E6007E">  |  </span> 03303350400</p>',
      mailFileAttachment: null,
      activeReplyPrompt: false,
      mailReplyTo: [],
      mailReplySubject: '',
      mailReplyCC: [],
      mailReplyBCC: [],
      mailReplyMessage: '',
      mailReplyFileAttachment: null,
      activeForwardPrompt: false,
      mailForwardTo: [],
      mailForwardSubject: '',
      mailForwardCC: [],
      mailForwardBCC: [],
      mailForwardMessage: '',
      mailForwardFileAttachment: null,
      currentMail: {},
      editorOption: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike', 'link', 'blockquote', 'code-block'],
            [{'header': 1}, {'header': 2}],
            [{'list': 'ordered'}, {'list': 'bullet'}],
            [{'font': []}]
          ]
        },
        placeholder: 'Message'
      },
      settings: {
        maxScrollbarLength: 60,
        wheelSpeed: 0.30
      },
    }
  },
  computed: {
    validateForm() {
      return this.mailTo.length !== 0
    },
    validateReplyForm() {
      return this.mailReplyTo.length !== 0;
    },
    validateForwardForm() {
      return this.mailForwardTo.length !== 0;
    },
    baseUrl() {
      const path = this.$route.path;
      return path.slice(0, path.lastIndexOf('/'))
    },
    scrollbarTag() {
      return this.isTouchDevice() ? 'div' : 'VuePerfectScrollbar';
    },
    focusedMailFolders() {
      if ((this.$route.path === "/email/inbox" || !this.checkFolderIdValid()) && this.$store.state.email.mailFolders.focused.length) {
        this.$router.push({
          path: "/email/" + this.$store.state.email.mailFolders.focused.length ? this.$store.state.email.mailFolders.focused[0].id : 'inbox'
        });
      }
      return this.$store.state.email.mailFolders.focused;
    },
    otherMailFolders() {
      return this.$store.state.email.mailFolders.others;
    }
  },
  watch: {
    isShowReplyPrompt(to, from) {
      if (to) {
        this.activeReplyPrompt = to;
        this.setReplyPromptForm();
        setTimeout(function () {
          this.mailReplyFileAttachment = this.$refs.reply_file_attachment;
        }.bind(this), 10);
      }
    },
    isShowForwardPrompt(to, from) {
      if (to) {
        this.activeForwardPrompt = to;
        this.setForwardPromptForm();
        setTimeout(function () {
          this.mailForwardFileAttachment = this.$refs.forward_file_attachment;
        }.bind(this), 10);
      }
    },
    activeReplyPrompt(to, from) {
      if (!to) {
        this.$emit('hideReplyPrompt');
      }
    },
    activeForwardPrompt(to, from) {
      if (!to) {
        this.$emit('hideForwardPrompt');
      }
    },
    openMailId(to, from) {
      if (to !== null) {
        this.currentMail = JSON.parse(JSON.stringify(this.$store.getters['email/getMail'](this.openMailId)));
        this.setReplyPromptForm();
        this.setForwardPromptForm();
      }
    }
  },
  methods: {
    checkFolderIdValid() {
      let validFlag = false;
      for (let i = 0; i < this.$store.state.email.mailFolders.others.length; i++) {
        if (this.$store.state.email.mailFolders.others[i].id === this.mailFilter) {
          validFlag = true;
        }
      }
      return validFlag;
    },
    clearFields() {
      this.$nextTick(() => {
        this.mailTo = [];
        this.mailSubject = '';
        this.mailCC = [];
        this.mailBCC = [];
        this.mailMessage = '<br><br><p style="font-family: Calibri,sans-serif; font-size: 16px; color: #333333;">Kind Regards - ' + this.$store.state.auth.me.first_name + ' ' +
            this.$store.state.auth.me.surname + ' <span style="color: #E6007E">  |  </span> 03303350400</p>';
      })
    },
    fileToByteString(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = error => reject(error);
      });
    },
    clearReplyFields() {
      this.mailReplyTo = [];
      this.mailReplySubject = '';
      this.mailReplyCC = [];
      this.mailReplyBCC = [];
      this.mailReplyMessage = '';
    },
    clearForwardFields() {
      this.mailForwardTo = [];
      this.mailForwardSubject = '';
      this.mailForwardCC = [];
      this.mailForwardBCC = [];
      this.mailForwardMessage = '';
    },
    sendMail() {
      let that = this;
      this.getMailObject().then(function (obj) {
        axios.post(that.crmApiPath + 'msgraph/emails/send', obj).then(response => {
          if (response.data.is_redirect) {
            window.location.href = response.data.redirect_url;
          }
          else {}
        }).catch(error => console.log(error));
        that.clearFields();
      });

    },
    sendReply() {
      let that = this;
      this.getReplyMailObject().then(function (obj) {
        axios.post(that.crmApiPath + 'msgraph/emails/' + that.currentMail.id + '/reply', obj).then(response => {
          if (response.data.is_redirect) {
            window.location.href = response.data.redirect_url;
          }
          else {}
        }).catch(error => console.log(error));
      });
    },
    sendForward() {
      let that = this;
      this.getForwardMailObject().then(function (obj) {
        axios.post(that.crmApiPath + 'msgraph/emails/' + that.currentMail.id + '/forward', obj).then(response => {
          if (response.data.is_redirect) {
            window.location.href = response.data.redirect_url;
          }
          else {}
        }).catch(error => console.log(error));
      });
    },
    async getMailObject() {
      let attachments = [];
      let removedItems = this.mailFileAttachment.itemRemove;
      let filesx = this.mailFileAttachment.filesx;
      for (let i = 0; i < filesx.length; i++) {
        if (!removedItems.includes(i)) {
          attachments.push({
            "@odata.type": "microsoft.graph.fileAttachment",
            name: filesx[i].name,
            contentType: filesx[i].type,
            contentBytes: await this.fileToByteString(filesx[i])
          });
        }
      }

      // BODY
      let msg = this.mailMessage.replaceAll("<p>", "<p style='font-family: Calibri,sans-serif; font-size: 16px; color: #333333'>");
      msg = msg.replaceAll("<h1>", "<h1 style='font-family: Calibri,sans-serif; font-size: 21px !important; color: #333333'>");
      msg = msg.replaceAll("<h2>", "<h2 style='font-family: Calibri,sans-serif; font-size: 19px !important; color: #333333'>");
      msg = msg.replaceAll("<li>", "<li style='font-family: Calibri,sans-serif; font-size: 16px; color: #333333'>");
      msg = '<br>' + msg;

      return {
        "email_from_id": this.$store.state.auth.me.id,
        "message": {
          "subject": this.mailSubject,
          "body": {
            "contentType": "HTML",
            "content": msg
          },
          "toRecipients": this.mailTo.map(function (elem) {
            return {
              emailAddress: {
                address: elem
              }
            }
          }),
          "ccRecipients": this.mailCC.map(function (elem) {
            return {
              emailAddress: {
                address: elem
              }
            }
          }),
          "bccRecipients": this.mailBCC.map(function (elem) {
            return {
              emailAddress: {
                address: elem
              }
            }
          }),
          "attachments": attachments
        }
      };
    },
    setReplyPromptForm() {
      this.clearReplyFields();
      this.mailReplyTo.push(this.currentMail.sender.emailAddress.address);
      this.mailReplySubject = "RE: " + this.currentMail.subject;
      this.mailReplyCC = this.currentMail.ccRecipients.map(function (elem) {
        return elem.emailAddress.address;
      });
      this.mailReplyBCC = this.currentMail.bccRecipients.map(function (elem) {
        return elem.emailAddress.address;
      });
    },
    setForwardPromptForm() {
      this.clearForwardFields();
      this.mailForwardSubject = "FWD: " + this.currentMail.subject;
      this.mailForwardCC = this.currentMail.ccRecipients.map(function (elem) {
        return elem.emailAddress.address;
      });
      this.mailForwardBCC = this.currentMail.bccRecipients.map(function (elem) {
        return elem.emailAddress.address;
      });
    },
    async getReplyMailObject() {
      let attachments = [];
      let removedItems = this.mailReplyFileAttachment.itemRemove;
      let filesx = this.mailReplyFileAttachment.filesx;
      for (let i = 0; i < filesx.length; i++) {
        if (!removedItems.includes(i)) {
          attachments.push({
            "@odata.type": "microsoft.graph.fileAttachment",
            name: filesx[i].name,
            contentType: filesx[i].type,
            contentBytes: await this.fileToByteString(filesx[i])
          });
        }
      }
      return {
        "message": {
          "subject": this.mailReplySubject,
          "toRecipients": this.mailReplyTo.map(function (elem) {
            return {
              emailAddress: {
                address: elem
              }
            }
          }),
          "ccRecipients": this.mailReplyCC.map(function (elem) {
            return {
              emailAddress: {
                address: elem
              }
            }
          }),
          "bccRecipients": this.mailReplyBCC.map(function (elem) {
            return {
              emailAddress: {
                address: elem
              }
            }
          }),
          "attachments": attachments
        },
        "comment": this.mailReplyMessage
      };
    },
    async getForwardMailObject() {

      let attachments = [];
      let removedItems = this.mailForwardFileAttachment.itemRemove;
      let filesx = this.mailForwardFileAttachment.filesx;
      for (let i = 0; i < filesx.length; i++) {
        if (!removedItems.includes(i)) {
          attachments.push({
            "@odata.type": "microsoft.graph.fileAttachment",
            name: filesx[i].name,
            contentType: filesx[i].type,
            contentBytes: await this.fileToByteString(filesx[i])
          });
        }
      }
      return {
        "message": {
          "subject": this.mailForwardSubject,
          "toRecipients": this.mailForwardTo.map(function (elem) {
            return {
              emailAddress: {
                address: elem
              }
            }
          }),
          "ccRecipients": this.mailForwardCC.map(function (elem) {
            return {
              emailAddress: {
                address: elem
              }
            }
          }),
          "bccRecipients": this.mailForwardBCC.map(function (elem) {
            return {
              emailAddress: {
                address: elem
              }
            }
          }),
          "attachments": attachments
        },
        "comment": this.mailForwardMessage
      };
    },
    isTouchDevice() {
      const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
      const mq = function (query) {
        return window.matchMedia(query).matches
      };
      if ('ontouchstart' in window || window.DocumentTouch) {
        return true
      }
      const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
      return mq(query);
    },
    showComposeModal() {
      this.activePrompt = true;
      setTimeout(function () {
        this.mailFileAttachment = this.$refs.new_file_attachment;
      }.bind(this), 10);
    },
  }
}
</script>
